import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, ShoppingCart } from "lucide-react";
import api, { MEDIA } from "../lib/api";
import { useCart } from "../contexts/CartContext";

export default function PackageDetail() {
    const { slug } = useParams();
    const [pkg, setPkg] = React.useState(null);
    const [products, setProducts] = React.useState([]);
    const { addItem } = useCart();

    React.useEffect(() => {
        api.get(`/packages/${slug}`).then(async r => {
            setPkg(r.data);
            const all = await api.get(`/products?limit=200`);
            const allProds = all.data.items || [];
            const prods = (r.data.items || []).map(it => {
                const p = allProds.find(x => x.product_id === it.product_id);
                return p ? { ...p, quantity: it.quantity } : null;
            }).filter(Boolean);
            setProducts(prods);
        }).catch(() => setPkg(null));
    }, [slug]);

    if (!pkg) return (
        <div className="container-x py-20 text-center">
            <div className="skeleton h-6 w-32 mx-auto rounded-full" />
        </div>
    );

    const price = pkg.sale_price || pkg.price;
    const onSale = pkg.sale_price && pkg.sale_price < pkg.price;
    const discount = onSale ? Math.round((1 - pkg.sale_price / pkg.price) * 100) : 0;

    return (
        <div className="container-x py-8 sm:py-12 animate-fade-up" data-testid="package-detail">
            <Link to="/pachete" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
                <ArrowLeft className="h-4 w-4" /> Înapoi la pachete
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                {/* Image */}
                <div className="relative rounded-2xl overflow-hidden bg-muted aspect-[4/3]">
                    {pkg.image
                        ? <img src={MEDIA(pkg.image)} alt={pkg.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />}
                    {onSale && (
                        <div className="absolute top-4 left-4 bg-primary text-primary-foreground text-sm font-bold px-3 py-1 rounded-full shadow-md">
                            -{discount}%
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex flex-col justify-center">
                    <div className="overline mb-2">Pachet complet</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">{pkg.name}</h1>

                    {pkg.includes_installation && (
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-full mb-5 w-fit">
                            <Check className="h-4 w-4" /> Instalare inclusă
                        </div>
                    )}

                    <p className="text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">{pkg.description}</p>

                    <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-border">
                        {onSale && (
                            <div className="text-lg text-muted-foreground line-through">{pkg.price.toFixed(2)} RON</div>
                        )}
                        <div className="font-heading text-4xl font-bold text-primary">
                            {price.toFixed(2)} <span className="text-base font-normal text-foreground/60">RON</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => addItem({ package_id: pkg.package_id, quantity: 1, unit_price: price, name: pkg.name, image: pkg.image || "", wants_installation: true })}
                            className="btn-primary flex items-center gap-2 text-base px-8 py-3.5"
                            data-testid="pkg-add-to-cart">
                            <ShoppingCart className="h-5 w-5" /> Comandă pachetul
                        </button>
                        <Link to="/programare-instalare" className="btn-outline text-base px-6 py-3.5" data-testid="pkg-schedule">
                            Programează instalare
                        </Link>
                    </div>
                </div>
            </div>

            {/* Package contents */}
            {products.length > 0 && (
                <div className="mt-16">
                    <h2 className="font-heading text-2xl sm:text-3xl mb-6">Ce conține pachetul</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {products.map((p, i) => (
                            <div key={i} className="flex gap-4 bg-card border border-border rounded-2xl p-4 hover:border-primary/30 transition-colors">
                                <div className="w-20 h-20 bg-muted rounded-xl overflow-hidden flex-shrink-0">
                                    {p.images?.[0] && <img src={MEDIA(p.images[0])} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-muted-foreground font-mono mb-0.5">{p.code} × {p.quantity}</div>
                                    <div className="font-semibold text-sm leading-snug">{p.name}</div>
                                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.short_description}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
